import type { CreasePattern } from '@/origami/types';
import { deriveTopology, type DerivedTopology } from '@/origami/topology';
import { analyzeKawasaki, type KawasakiResult } from './kawasaki';
import { analyzeMaekawa, type MaekawaResult } from './maekawa';
import { buildRigidSystem, type RigidSystem } from './jacobian';
import { estimateDegreesOfFreedom, type DofResult } from './degreesOfFreedom';
import { polygonArea } from '@/geometry/polygon';
import { AREA_EPSILON } from '@/constants/numeric';
import { resolveTargetAngle } from '@/origami/angles';

export type IssueSeverity = 'error' | 'warning' | 'info';

export interface AnalysisIssue {
  id: string;
  severity: IssueSeverity;
  title: string;
  detail: string;
  /** Geometry the UI can jump to. */
  target?: { kind: 'vertex' | 'edge' | 'face'; index: number };
}

export interface VertexAnalysis {
  vertex: number;
  kawasaki: KawasakiResult;
  maekawa: MaekawaResult;
}

export interface PatternAnalysis {
  topology: DerivedTopology;
  rigidSystem: RigidSystem;
  vertices: VertexAnalysis[];
  interiorVertexCount: number;
  kawasakiPassing: number;
  kawasakiApplicable: number;
  maekawaPassing: number;
  maekawaApplicable: number;
  /** True only when every interior vertex passes both necessary conditions. */
  allLocalConditionsPass: boolean;
  dof: DofResult;
  issues: AnalysisIssue[];
  counts: {
    vertices: number;
    edges: number;
    faces: number;
    mountains: number;
    valleys: number;
    boundary: number;
    cuts: number;
    unassigned: number;
  };
  totalArea: number;
}

/**
 * Full static analysis of a crease pattern. Cheap enough to run on every edit
 * for hand-drawn patterns; the caller throttles it for large imports.
 *
 * IMPORTANT WORDING RULE (enforced by the strings below): Kawasaki and Maekawa
 * are *necessary local* conditions. Passing them at every vertex does not make
 * a pattern globally flat-foldable - deciding that is NP-hard. The UI must never
 * claim otherwise.
 */
export function analyzePattern(pattern: CreasePattern): PatternAnalysis {
  const topology = deriveTopology(pattern);
  const issues: AnalysisIssue[] = [];

  const vertices: VertexAnalysis[] = [];
  let kawasakiPassing = 0;
  let kawasakiApplicable = 0;
  let maekawaPassing = 0;
  let maekawaApplicable = 0;
  let interiorVertexCount = 0;

  for (const v of topology.vertices) {
    const kawasaki = analyzeKawasaki(v);
    const maekawa = analyzeMaekawa(pattern, v);
    vertices.push({ vertex: v.index, kawasaki, maekawa });
    if (v.interior) interiorVertexCount++;
    if (kawasaki.applicable) {
      kawasakiApplicable++;
      if (kawasaki.satisfied) kawasakiPassing++;
      else
        issues.push({
          id: `kawasaki-${v.index}`,
          severity: 'warning',
          title: `Vertex ${v.index}: Kawasaki residual ${kawasaki.residualDegrees.toFixed(3)}°`,
          detail:
            'The alternating sum of sector angles is not zero, so this vertex cannot fold flat. It may still fold rigidly to a non-flat state.',
          target: { kind: 'vertex', index: v.index },
        });
    } else if (v.interior && kawasaki.notApplicableReason === 'odd-degree') {
      issues.push({
        id: `kawasaki-odd-${v.index}`,
        severity: 'warning',
        title: `Vertex ${v.index} has odd degree (${v.degree})`,
        detail:
          'An interior vertex of odd degree can never fold flat: Maekawa forces M + V to be even.',
        target: { kind: 'vertex', index: v.index },
      });
    }
    if (maekawa.applicable) {
      maekawaApplicable++;
      if (maekawa.satisfied) maekawaPassing++;
      else
        issues.push({
          id: `maekawa-${v.index}`,
          severity: 'warning',
          title: `Vertex ${v.index}: |M - V| = ${Math.abs(maekawa.difference)}, expected 2`,
          detail: `${maekawa.mountains} mountain and ${maekawa.valleys} valley creases meet here. A flat-folded interior vertex always satisfies |M - V| = 2.`,
          target: { kind: 'vertex', index: v.index },
        });
    } else if (v.interior && maekawa.notApplicableReason === 'unassigned-creases') {
      issues.push({
        id: `maekawa-unassigned-${v.index}`,
        severity: 'info',
        title: `Vertex ${v.index} has creases without an M/V assignment`,
        detail:
          'Maekawa’s count is inconclusive until every crease at this vertex is a mountain or a valley.',
        target: { kind: 'vertex', index: v.index },
      });
    }

    if (
      v.degree === 1 &&
      v.edges.some((e) => pattern.edgesAssignment[e] === 'C')
    ) {
      issues.push({
        id: `open-slit-${v.index}`,
        severity: 'error',
        title: `Vertex ${v.index}: a cut stops inside the paper`,
        detail:
          'fold.app does not yet model slits that end in the middle of a sheet: the surrounding region stops being a simple polygon and cannot be triangulated. Extend the cut to the boundary or to another cut.',
        target: { kind: 'vertex', index: v.index },
      });
    } else if (v.degree === 1) {
      issues.push({
        id: `dangling-${v.index}`,
        severity: 'warning',
        title: `Vertex ${v.index} is a dangling crease end`,
        detail:
          'A crease that stops in the middle of the paper cannot fold: it would tear the sheet. Extend it to the boundary or to another crease.',
        target: { kind: 'vertex', index: v.index },
      });
    }
  }

  for (let f = 0; f < pattern.facesVertices.length; f++) {
    const loop = pattern.facesVertices[f];
    const area = polygonArea(loop.map((i) => pattern.verticesCoords[i]));
    if (area < AREA_EPSILON) {
      issues.push({
        id: `degenerate-face-${f}`,
        severity: 'error',
        title: `Face ${f} is degenerate`,
        detail: 'The face has zero area and cannot be triangulated or simulated.',
        target: { kind: 'face', index: f },
      });
    }
  }

  if (pattern.facesVertices.length === 0 && pattern.edgesVertices.length > 0) {
    issues.push({
      id: 'no-faces',
      severity: 'error',
      title: 'No closed faces were found',
      detail:
        'The crease pattern does not enclose any region. Add a boundary, or check that the boundary edges actually meet.',
    });
  }

  const rigidSystem = buildRigidSystem(pattern, topology);
  // Perturb along the pattern's own intended folding direction rather than an
  // arbitrary one: the flat state is a bifurcation point, and the rank there
  // depends on which branch you approach it from.
  const direction = new Float64Array(rigidSystem.variableCount);
  for (let i = 0; i < rigidSystem.variableCount; i++) {
    const e = rigidSystem.variableEdges[i];
    direction[i] = Math.sign(
      resolveTargetAngle(pattern.edgesAssignment[e], pattern.edgesFoldAngle[e]),
    );
  }
  const dof = estimateDegreesOfFreedom(
    rigidSystem,
    new Float64Array(rigidSystem.variableCount),
    direction,
  );

  const counts = {
    vertices: pattern.verticesCoords.length,
    edges: pattern.edgesVertices.length,
    faces: pattern.facesVertices.length,
    mountains: 0,
    valleys: 0,
    boundary: 0,
    cuts: 0,
    unassigned: 0,
  };
  for (const a of pattern.edgesAssignment) {
    if (a === 'M') counts.mountains++;
    else if (a === 'V') counts.valleys++;
    else if (a === 'B') counts.boundary++;
    else if (a === 'C') counts.cuts++;
    else if (a === 'U') counts.unassigned++;
  }

  let totalArea = 0;
  for (const loop of pattern.facesVertices) {
    totalArea += polygonArea(loop.map((i) => pattern.verticesCoords[i]));
  }

  return {
    topology,
    rigidSystem,
    vertices,
    interiorVertexCount,
    kawasakiPassing,
    kawasakiApplicable,
    maekawaPassing,
    maekawaApplicable,
    allLocalConditionsPass:
      kawasakiApplicable > 0 &&
      kawasakiPassing === kawasakiApplicable &&
      maekawaPassing === maekawaApplicable,
    dof,
    issues,
    counts,
    totalArea,
  };
}
